import { useState, useEffect, useMemo } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { useLocation } from "react-router-dom";
import { QuotationQuery, TblQuote, TblQuoteBy } from "features/quotation/types/quotation";
import { getQuoteByListApi, getQuotationListApi } from "features/quotation/api/quotationListApi";
import { TOption } from "type";

const defaultQueryParams = {
  quote_no: "",
  customer_name: "",
  customer_tel_no: "",
  store_or_company: "",
  quote_by: "",
  created_at_start: "",
  created_at_end: "",
  delete_flag: 0,
  expired_flg: 1,
  limit: DEFAULT_LIMIT_ITEMS,
  offset: 1,
};

const groupAndDedupQuotations = (list: TblQuote[]) => {
  const grouped = Object.groupBy(list, (item) => item.quote_id);
  return Object.values(grouped)
    .map((items) => (items && items.length > 0 ? items[0] : null))
    .filter((item): item is TblQuote => item !== null)
    .sort((a, b) => b.quote_id - a.quote_id);
};

/**
 * Custom hook to manage and fetch the quotation list and related state.
 */
const useQuotationList = () => {
  const location = useLocation();
  const [queryParams, setQueryParams] = useState<QuotationQuery>(defaultQueryParams);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [quotations, setQuotations] = useState<TblQuote[]>([]);
  const [quoteByList, setQuoteByList] = useState<TOption[]>([]);

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  const getQuotations = async (body?: QuotationQuery) => {
    setLoading(true);
    try {
      const prevScrollY = document.documentElement.scrollTop || document.body.scrollTop;
      const data = await getQuotationListApi(body);
      let newList: TblQuote[];
      if (body && body?.offset > 1) {
        newList = groupAndDedupQuotations([...quotations, ...data.list]);
      } else {
        newList = groupAndDedupQuotations(data.list);
      }
      setQuotations(newList);
      setTotal(data.total);
      window.scrollTo(0, prevScrollY || 0);
    } catch (error) {
      // do nothing
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches a list of quotes and updates the state with the retrieved list.
   * Handles errors silently and updates loading state.
   */
  const getQuoteBy = async () => {
    try {
      const data = await getQuoteByListApi();
      if (data.list.length > 0) {
        const formattedList = data.list.map((item: TblQuoteBy) => ({
          label: item.quote_by,
          value: item.quote_by,
        }));
        setQuoteByList(formattedList);
      }
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  useEffect(() => {
    const params = { ...queryParams };

    Object.keys(params).forEach((key) => {
      const value = params[key as keyof typeof params];
      if (value === "" || value === null || value === undefined) {
        delete params[key as keyof typeof params];
      }
    });
  }, [queryParams]);

  useEffect(() => {
    window.addEventListener("beforeunload", () => {
      window.history.replaceState({}, "");
    });

    const oldParams = (location.state as any)?.queryParams;

    setQueryParams((prev) => ({
      ...prev,
      ...oldParams,
      offset: 1,
    }));

    getQuotations({ ...queryParams, ...oldParams, offset: 1 });
    getQuoteBy();
  }, []);

  const handleSearch = () => {
    getQuotations({
      ...queryParams,
      offset: 1,
    });
  };

  const onLoadMore = async () => {
    if (page + 1 > totalPage) return;
    setLoading(true);
    setPage((prevPage) => prevPage + 1);
    await getQuotations({ ...queryParams, offset: page + 1 });
  };

  return {
    quotations,
    queryParams,
    total,
    totalPage,
    page,
    loading,
    handleSearch,
    onLoadMore,
    setQueryParams,
    quoteByList,
  };
};

export default useQuotationList;
